import { useQuery } from '@tanstack/react-query';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { Avatar } from '../../components/ui/Avatar';
import { api } from '../../lib/api';
import { FollowButton } from './FollowButton';
import type { Profile } from './types';
import { Verified } from '../../components/ui/Verified';

/**
 * A peek at an artist, without going anywhere.
 *
 * ## Why a third tier
 *
 * There were two ways to find out who somebody is: read the name, or open
 * their profile. The first tells you nothing and the second costs a page. Most
 * of the time the question is small - do I know them, how many people follow
 * them, is that the person who sent this - and it does not deserve a
 * navigation. Below this sits `ArtistDrawer`, which is the answer when the
 * question is "what else have they made".
 *
 * ## Pointers only
 *
 * A hover card on a touch screen is either dead weight or a trap: there is no
 * hover, so it either never opens or it hijacks the tap that was meant for the
 * link. `(hover: hover) and (pointer: fine)` is the honest test, and when it
 * fails this renders the children and attaches nothing at all - no timers, no
 * listeners, no query.
 *
 * ## It warms the cache it is standing in front of
 *
 * The query key is the one `ProfilePage` reads, so a hover that turns into a
 * click arrives at a page whose data is already there. The hover pays for the
 * navigation that usually follows it.
 */
interface Props {
  username: string;
  children: ReactNode;
  /** Applied to the inline wrapper, so it can inherit layout from its parent. */
  className?: string;
}

/** How long a pointer must rest before this is a request rather than a passage. */
const OPEN_MS = 450;

/**
 * And how long it may leave before the card goes.
 *
 * Long enough to cross the gap between the name and the card, which is the
 * whole reason a grace period exists.
 */
const CLOSE_MS = 200;

/** Clear of the viewport edge, and of the trigger. */
const MARGIN = 12;
const GAP = 8;

export function ArtistHoverCard({ username, children, className = '' }: Props) {
  const fine = useFinePointer();

  // Not a pointer device: hand back exactly what was passed in.
  if (!fine) return <>{children}</>;

  return (
    <Hoverable username={username} className={className}>
      {children}
    </Hoverable>
  );
}

/**
 * Whether this input can hover at all.
 *
 * Read once and then watched, because a laptop with a touchscreen can change
 * its answer and a tablet with a trackpad attached certainly does.
 */
function useFinePointer(): boolean {
  const [fine, setFine] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches,
  );

  useEffect(() => {
    const query = window.matchMedia('(hover: hover) and (pointer: fine)');
    const onChange = () => setFine(query.matches);

    query.addEventListener('change', onChange);

    return () => query.removeEventListener('change', onChange);
  }, []);

  return fine;
}

function Hoverable({ username, children, className }: Required<Props>) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const trigger = useRef<HTMLSpanElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);

  const { data } = useQuery({
    // The key `ProfilePage` uses. See the note on the component.
    queryKey: ['profile', username],
    queryFn: () => api.get<Profile>(`/users/${username}`),
    enabled: open,
    staleTime: 60_000,
  });

  const schedule = (next: boolean, delay: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(next), delay);
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  /**
   * Put the card where it fits, which is not always where it was asked to go.
   *
   * Below the name by preference, above it when the bottom of the window is
   * closer than the card is tall, and never past either side. A panel that
   * opens half off the screen is the bug this codebase has already shipped
   * once, on the notification bell.
   */
  const place = useCallback(() => {
    const anchor = trigger.current?.getBoundingClientRect();
    const box = card.current?.getBoundingClientRect();

    if (!anchor || !box) return;

    const left = Math.max(
      MARGIN,
      Math.min(anchor.left, window.innerWidth - box.width - MARGIN),
    );

    const below = anchor.bottom + GAP;
    const above = anchor.top - box.height - GAP;
    const fitsBelow = below + box.height <= window.innerHeight - MARGIN;

    setPos({
      left,
      top: fitsBelow ? below : Math.max(MARGIN, above),
    });
  }, []);

  // Re-placed when the card appears and again when its contents arrive, since
  // a skeleton and a loaded profile are not the same height.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, data, place]);

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  return (
    <>
      <span
        ref={trigger}
        data-artist-hover={username}
        className={className}
        onMouseEnter={() => schedule(true, OPEN_MS)}
        onMouseLeave={() => schedule(false, CLOSE_MS)}
        /* Keyboard users get it too - it is the same information, and the
           alternative is that it exists only for people using a mouse. */
        onFocus={() => schedule(true, 0)}
        onBlur={() => schedule(false, 0)}
      >
        {children}
      </span>

      {open &&
        createPortal(
          <div
            ref={card}
            role="tooltip"
            data-artist-hovercard={username}
            onMouseEnter={() => window.clearTimeout(timer.current)}
            onMouseLeave={() => schedule(false, CLOSE_MS)}
            style={{
              left: pos?.left ?? -9999,
              top: pos?.top ?? -9999,
              // Hidden until measured, so it is never seen in the wrong place.
              visibility: pos ? 'visible' : 'hidden',
            }}
            className="fixed z-50 w-80 rounded-[var(--radius-card)] border border-line bg-surface p-4 shadow-xl"
          >
            {data ? <Card profile={data} /> : <Loading />}
          </div>,
          /*
           * The body, rather than beside the name.
           *
           * Names live inside cards, rows and docks, and several of those clip
           * their overflow - a card rendered in place would be cut off by
           * whichever ancestor happened to be holding it.
           */
          document.body,
        )}
    </>
  );
}

function Card({ profile }: { profile: Profile }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <Link to={`/${profile.username}`} className="shrink-0">
          <Avatar src={profile.avatar?.medium ?? null} name={profile.display_name} size={52} />
        </Link>

        <div className="min-w-0 flex-1">
          <Link
            to={`/${profile.username}`}
            className="flex min-w-0 items-center gap-1.5 hover:underline"
          >
            <span className="truncate text-[15px] font-semibold text-ink">
              {profile.display_name}
              {profile?.verified && <Verified />}
            </span>
            {profile.online && (
              <span
                aria-label="Online"
                className="h-2 w-2 shrink-0 rounded-full bg-fresh"
              />
            )}
          </Link>

          <div className="meta truncate text-faint">
            @{profile.username}
            {profile.location ? ` · ${profile.location}` : ''}
          </div>
        </div>
      </div>

      {profile.bio && (
        <p className="line-clamp-2 text-[13px] leading-relaxed text-text-2">{profile.bio}</p>
      )}

      <div className="meta-caps flex items-center gap-4 text-faint">
        <span>
          <span className="text-ink">{profile.follower_count.toLocaleString()}</span> followers
        </span>
        <span>
          <span className="text-ink">{profile.track_count.toLocaleString()}</span>{' '}
          {profile.track_count === 1 ? 'track' : 'tracks'}
        </span>
      </div>

      {/* Returns nothing for your own name and for signed-out readers, so the
          card simply ends above it rather than showing a button that cannot
          be pressed. */}
      <FollowButton profile={profile} />
    </div>
  );
}

/** The same shape, so the card does not jump when the answer arrives. */
function Loading() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true">
      <div className="flex items-center gap-3">
        <div className="h-[52px] w-[52px] shrink-0 rounded-full bg-raised" />
        <div className="flex flex-1 flex-col gap-2">
          <div className="h-3.5 w-32 rounded bg-raised" />
          <div className="h-3 w-20 rounded bg-raised" />
        </div>
      </div>
      <div className="h-3 w-full rounded bg-raised" />
      <div className="h-3 w-2/3 rounded bg-raised" />
    </div>
  );
}
